import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext";
import { db, firebaseConfig } from "../firebaseConfig"; 
import { doc, setDoc, deleteDoc, updateDoc, collection, getDocs, query } from "firebase/firestore";
import * as XLSX from "xlsx";
import Loader from "../components/Loader";
import toast from 'react-hot-toast'; 

// Secondary Auth Imports
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";

function ManageUsers() {
  const { users, loading } = useWebData();

  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false); 
  
  const [formData, setFormData] = useState({
      // Official
      name: "", email: "", mobile: "", role: "Sales Executive",
      empId: "", joiningDate: "", 
      monthlyTarget: "0", yearlyLeaves: "18", 
      password: "",

      // Personal
      personalEmail: "", personalMobile: "", bloodGroup: "",
      
      // Address
      address: "", city: "", state: "", permanentAddress: "",

      // Documents & Bank
      aadhar: "", pan: "", 
      bankName: "", accountNo: "", ifscCode: "",
      
      // Assets
      assetNotes: ""
  });

  // 1. SEARCH
  useEffect(() => {
    let data = users || [];
    if (searchTerm) {
        const keywords = searchTerm.toLowerCase().split(" ").filter(w => w);
        const result = data.filter(i => {
            const row = `${i.name} ${i.email} ${i.role} ${i.empId} ${i.mobile} ${i.city}`.toLowerCase();
            return keywords.every(w => row.includes(w));
        });
        setFiltered(result);
    } else {
        setFiltered(data);
    }
  }, [users, searchTerm]);

  // 🔥 2. CUSTOM ID GENERATOR (LMS-YYYY-XX)
  const generateNextEmpId = async () => {
      try {
          const currentYear = new Date().getFullYear();
          const prefix = `LMS-${currentYear}-`;
          const q = query(collection(db, "users"));
          const snapshot = await getDocs(q);
          
          const existingIds = snapshot.docs
              .map(doc => doc.data().empId || "")
              .filter(id => id.startsWith(prefix));

          let maxNum = 0;
          existingIds.forEach(id => {
              const parts = id.split('-'); 
              if (parts.length === 3) {
                  const num = parseInt(parts[2]);
                  if (!isNaN(num) && num > maxNum) maxNum = num;
              }
          });

          const nextNum = maxNum + 1;
          const formattedId = `${prefix}${String(nextNum).padStart(2, '0')}`;
          
          setFormData(prev => ({ ...prev, empId: formattedId }));

      } catch (error) {
          console.error("ID Gen Error:", error);
          const fallback = `LMS-${new Date().getFullYear()}-${Math.floor(Math.random()*1000)}`;
          setFormData(prev => ({ ...prev, empId: fallback }));
      }
  };

  // 🔥 3. MAIN LOGIC: SAVE USER
  const handleSave = async () => {
      if(!formData.email || !formData.name) return toast.error("Name & Official Email required!");
      if(!editData && !formData.password) return toast.error("Password is required for new users!");
      if(!editData && formData.password.length < 6) return toast.error("Password must be 6+ chars");

      setIsProcessing(true);

      try {
          // Construct Bank Details String for backward compatibility if needed, or store separate
          const bankDetailsFull = `${formData.bankName} - ${formData.accountNo} (${formData.ifscCode})`;

          const payload = {
              ...formData,
              bankDetails: bankDetailsFull, // Saving combined for Profile display compatibility
              monthlyTarget: Number(formData.monthlyTarget),
              yearlyLeaves: Number(formData.yearlyLeaves)
          };
          
          delete payload.password; 

          if (editData) {
              // --- UPDATE MODE ---
              await updateDoc(doc(db, "users", editData.id), payload);
              await setDoc(doc(db, "settings", "targets"), { [formData.name]: Number(formData.monthlyTarget) }, { merge: true });
              toast.success("Profile Updated ✅");
          } else {
              // --- CREATE MODE ---
              const secondaryApp = initializeApp(firebaseConfig, "Secondary");
              const secondaryAuth = getAuth(secondaryApp);

              try {
                  await createUserWithEmailAndPassword(secondaryAuth, formData.email, formData.password);
                  await signOut(secondaryAuth); 
                  
                  await setDoc(doc(db, "users", formData.email.toLowerCase()), {
                      ...payload,
                      createdAt: new Date().toISOString(),
                      status: "Active"
                  });

                  await setDoc(doc(db, "settings", "targets"), { [formData.name]: Number(formData.monthlyTarget) }, { merge: true });

                  toast.success(`User Created! ID: ${formData.empId}`);
              } catch (authError) {
                  if(authError.code === 'auth/email-already-in-use') {
                      throw new Error("Email already registered!");
                  }
                  throw authError;
              }
          }
          closeModal();
      } catch (e) {
          toast.error(e.message);
      }
      setIsProcessing(false);
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete profile? (Login access remains until disabled in console)")) {
          await deleteDoc(doc(db, "users", id));
          toast.success("Profile Deleted");
      }
  };

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filtered.map(i => ({
        ID: i.empId, Name: i.name, Role: i.role, Email: i.email,
        Mobile: i.mobile, City: i.city, Target: i.monthlyTarget
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    XLSX.writeFile(wb, "Employee_Database.xlsx");
  };

  const openEdit = (item) => {
      setEditData(item);
      // Parse bank details if stored as string, else keep separate
      setFormData({...item, password: ""}); 
      setShowModal(true);
  };

  const openAdd = () => {
      setEditData(null);
      setFormData({
          name: "", email: "", mobile: "", role: "Sales Executive",
          empId: "Generating...", 
          joiningDate: new Date().toISOString().split('T')[0],
          address: "", city: "", state: "", permanentAddress: "",
          personalEmail: "", personalMobile: "", bloodGroup: "",
          aadhar: "", pan: "", 
          bankName: "", accountNo: "", ifscCode: "",
          assetNotes: "",
          monthlyTarget: "1000000", yearlyLeaves: "18", password: ""
      });
      setShowModal(true);
      generateNextEmpId(); 
  };

  const closeModal = () => {
      setShowModal(false);
      setEditData(null);
  };

  return (
    <div style={{padding:"20px", fontFamily:"Arial"}}>
      <div style={headerStyle}>
        <h2 style={{margin:0, fontSize:"18px", color:"#2c3e50"}}>👥 Employee Master</h2>
        <div style={{display:"flex", gap:"5px"}}>
            <input type="text" placeholder="🔍 Search..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={inputStyle} />
            <button onClick={openAdd} style={btnAdd}>➕ New Employee</button>
            <button onClick={downloadExcel} style={btnDownload}>📥 Data</button>
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto"}}>
          <table border="1" style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#2c3e50", color:"white"}}>
                    <th style={thStyle}>ID & Name</th>
                    <th style={thStyle}>Role & Date</th>
                    <th style={thStyle}>Official Contact</th>
                    <th style={thStyle}>Target / Leaves</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.map(i => (
                <tr key={i.id} style={{textAlign:"center", height:"40px"}}>
                    <td style={{...tdStyle, textAlign:"left"}}>
                        <div style={{fontWeight:"bold", color:"#2980b9"}}>{i.empId}</div>
                        <div style={{fontSize:"13px"}}>{i.name}</div>
                    </td>
                    <td style={tdStyle}>
                        <span style={roleBadge(i.role)}>{i.role}</span>
                        <div style={{fontSize:"10px", color:"gray", marginTop:"3px"}}>{i.joiningDate}</div>
                    </td>
                    <td style={{...tdStyle, textAlign:"left", fontSize:"11px"}}>
                        <div>📞 {i.mobile}</div>
                        <div>📧 {i.email}</div>
                    </td>
                    <td style={tdStyle}>
                        <div style={{color:"green", fontWeight:"bold"}}>₹ {Number(i.monthlyTarget).toLocaleString()}</div>
                        <div style={{fontSize:"10px"}}>Leaves: {i.yearlyLeaves}/Yr</div>
                    </td>
                    <td style={tdStyle}>
                        <button onClick={() => openEdit(i)} style={btnIconEdit}>✏️</button>
                        <button onClick={() => handleDelete(i.id)} style={btnIconDelete}>🗑️</button>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* BIG MODAL FORM */}
      {showModal && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#2c3e50"}}>{editData ? "✏️ Edit Employee" : "➕ Onboard New Employee"}</h3>
                <button onClick={closeModal} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={scrollForm}>
                {/* 1. ROLE & ID */}
                <h4 style={sectionHeader}>1. Official Details</h4>
                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Role</label>
                        <select value={formData.role} onChange={(e)=>setFormData({...formData, role:e.target.value})} style={modalInput}>
                            <option>Sales Executive</option>
                            <option>Service Engineer</option>
                            <option>Manager</option>
                            <option>Admin</option>
                            <option>Back Office</option>
                            <option>Accountant</option>
                            <option>Store Keeper</option>
                        </select>
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Employee ID</label>
                        <input type="text" value={formData.empId} readOnly style={{...modalInput, backgroundColor:"#e8f0fe", fontWeight:"bold"}} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Joining Date</label>
                        <input type="date" value={formData.joiningDate} onChange={(e)=>setFormData({...formData, joiningDate:e.target.value})} style={modalInput} />
                    </div>
                </div>

                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Full Name</label>
                        <input type="text" value={formData.name} onChange={(e)=>setFormData({...formData, name:e.target.value})} style={modalInput} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Official Email (Login)</label>
                        <input type="email" value={formData.email} onChange={(e)=>setFormData({...formData, email:e.target.value})} style={modalInput} disabled={!!editData} />
                    </div>
                </div>

                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Official Mobile</label>
                        <input type="text" value={formData.mobile} onChange={(e)=>setFormData({...formData, mobile:e.target.value})} style={modalInput} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Base City</label>
                        <input type="text" value={formData.city} onChange={(e)=>setFormData({...formData, city:e.target.value})} style={modalInput} />
                    </div>
                </div>

                {!editData && (
                    <div style={{marginBottom:"10px"}}>
                        <label style={{...labelStyle, color:"#c0392b"}}>Create Password *</label>
                        <input type="text" placeholder="Set user password" value={formData.password} onChange={(e)=>setFormData({...formData, password:e.target.value})} style={{...modalInput, border:"1px solid #c0392b"}} />
                    </div>
                )}

                {/* 2. PERSONAL DETAILS */}
                <h4 style={sectionHeader}>2. Personal Details</h4>
                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Personal Email</label>
                        <input type="email" value={formData.personalEmail} onChange={(e)=>setFormData({...formData, personalEmail:e.target.value})} style={modalInput} placeholder="Optional" />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Personal Mobile</label>
                        <input type="text" value={formData.personalMobile} onChange={(e)=>setFormData({...formData, personalMobile:e.target.value})} style={modalInput} placeholder="Optional" />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Blood Group</label>
                        <input type="text" value={formData.bloodGroup} onChange={(e)=>setFormData({...formData, bloodGroup:e.target.value})} style={modalInput} placeholder="e.g. O+" />
                    </div>
                </div>

                {/* 3. ADDRESS */}
                <h4 style={sectionHeader}>3. Address Information</h4>
                <div style={row}>
                    <div style={{...col, flex:2}}>
                        <label style={labelStyle}>Current Address</label>
                        <input type="text" value={formData.address} onChange={(e)=>setFormData({...formData, address:e.target.value})} style={modalInput} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>State</label>
                        <input type="text" value={formData.state} onChange={(e)=>setFormData({...formData, state:e.target.value})} style={modalInput} />
                    </div>
                </div>
                <div style={{marginBottom:"10px"}}>
                    <label style={labelStyle}>Permanent Address</label>
                    <input type="text" value={formData.permanentAddress} onChange={(e)=>setFormData({...formData, permanentAddress:e.target.value})} style={modalInput} placeholder="If different from current" />
                </div>

                {/* 4. BANK & DOCS */}
                <h4 style={sectionHeader}>4. Bank & Documents</h4>
                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Bank Name</label>
                        <input type="text" value={formData.bankName} onChange={(e)=>setFormData({...formData, bankName:e.target.value})} style={modalInput} placeholder="SBI / HDFC" />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Account Number</label>
                        <input type="text" value={formData.accountNo} onChange={(e)=>setFormData({...formData, accountNo:e.target.value})} style={modalInput} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>IFSC Code</label>
                        <input type="text" value={formData.ifscCode} onChange={(e)=>setFormData({...formData, ifscCode:e.target.value})} style={modalInput} />
                    </div>
                </div>
                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Aadhar No.</label>
                        <input type="text" value={formData.aadhar} onChange={(e)=>setFormData({...formData, aadhar:e.target.value})} style={modalInput} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>PAN No.</label>
                        <input type="text" value={formData.pan} onChange={(e)=>setFormData({...formData, pan:e.target.value})} style={modalInput} />
                    </div>
                </div>

                {/* 5. SETTINGS */}
                <h4 style={sectionHeader}>5. Work Settings</h4>
                <div style={row}>
                    <div style={col}>
                        <label style={labelStyle}>Monthly Target (₹)</label>
                        <input type="number" value={formData.monthlyTarget} onChange={(e)=>setFormData({...formData, monthlyTarget:e.target.value})} style={modalInput} />
                    </div>
                    <div style={col}>
                        <label style={labelStyle}>Yearly Leaves</label>
                        <input type="number" value={formData.yearlyLeaves} onChange={(e)=>setFormData({...formData, yearlyLeaves:e.target.value})} style={modalInput} />
                    </div>
                </div>
                
                <label style={labelStyle}>Assets Given (Notes)</label>
                <textarea value={formData.assetNotes} onChange={(e)=>setFormData({...formData, assetNotes:e.target.value})} style={{...modalInput, height:"50px"}} placeholder="e.g. Laptop (Dell), Tool Kit #45"></textarea>

            </div>

            <div style={{marginTop:"20px", textAlign:"right", borderTop:"1px solid #eee", paddingTop:"15px"}}>
                <button onClick={closeModal} style={btnCancel}>Cancel</button>
                <button onClick={handleSave} disabled={isProcessing} style={{...btnSave, opacity: isProcessing ? 0.7 : 1}}>
                    {isProcessing ? "Processing..." : (editData ? "Update Profile" : "Create Login & Profile")}
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// HELPER: Role Colors
const roleBadge = (role) => {
    let bg = "#95a5a6";
    if(role === 'Admin') bg = "#c0392b";
    if(role === 'Manager') bg = "#e67e22";
    if(role === 'Sales Executive') bg = "#27ae60";
    if(role === 'Service Engineer') bg = "#2980b9";
    return { padding:"3px 8px", borderRadius:"10px", fontSize:"10px", fontWeight:"bold", backgroundColor: bg, color:'white' };
};

// STYLES
const headerStyle={backgroundColor:"#f8f9fa", padding:"15px", borderRadius:"8px", marginBottom:"15px", display:"flex", justifyContent:"space-between", alignItems:"center"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"12.5px", whiteSpace:"nowrap"};
const thStyle={padding:"10px", border:"1px solid #ddd", backgroundColor:"#2c3e50", color:"white", textAlign:"center"};
const tdStyle={padding:"8px", border:"1px solid #ddd", verticalAlign:"middle"};
const inputStyle={padding:"8px", border:"1px solid #ccc", borderRadius:"4px", fontSize:"13px", width:"200px"};
const btnAdd={backgroundColor:"#2c3e50", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 10px", borderRadius:"3px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#e74c3c", color:"white", border:"none", padding:"5px 10px", borderRadius:"3px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.6)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"20px", borderRadius:"8px", width:"700px", maxHeight:"90vh", boxShadow:"0 5px 15px rgba(0,0,0,0.3)", display:"flex", flexDirection:"column"};
const scrollForm={overflowY:"auto", paddingRight:"10px"};
const modalInput={width:"100%", padding:"8px", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555", marginBottom:"3px", display:"block"};
const sectionHeader={fontSize:"13px", color:"#3b5998", borderBottom:"1px solid #eee", paddingBottom:"5px", marginTop:"15px", marginBottom:"10px", fontWeight:"bold"};
const row={display:"flex", gap:"10px", marginBottom:"10px"};
const col={flex:1};
const btnSave={backgroundColor:"#27ae60", color:"white", border:"none", padding:"10px 20px", borderRadius:"5px", cursor:"pointer", marginLeft:"5px", fontWeight:"bold"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"10px 20px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};

export default ManageUsers;